import { useState, useEffect } from 'react';

interface MobileStickyTopBarProps {
  quoteHref?: string;
  cityName?: string;
}

export default function MobileStickyTopBar({ quoteHref = '/book-appointment', cityName }: MobileStickyTopBarProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed in this session
    const dismissed = sessionStorage.getItem('topBarDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    const handleScroll = () => {
      setIsVisible(window.scrollY > 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('topBarDismissed', '1');
  };

  if (isDismissed) return null;

  const label = cityName
    ? `Book a Free Consultation — ${cityName}`
    : 'Book a Free Consultation';

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] md:hidden transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      role="banner"
      aria-label="Get a free life insurance quote"
    >
      <div className="bg-[#D97642] flex items-center justify-between px-3 py-2.5">
        {/* Left CTA */}
          <a
          href={quoteHref}
          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
        >
          <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
            <i className="ri-shield-check-fill text-white text-[14px]"></i>
          </div>
          <div className="min-w-0">
            <p className="text-white text-[12px] font-bold leading-tight truncate">{label}</p>
            <p className="text-white/85 text-[10px] leading-tight">15 min · No obligation · Local agent</p>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1 ml-1 bg-white/20 rounded-full px-2.5 py-1">
            <span className="text-white text-[11px] font-bold whitespace-nowrap">Book Now</span>
            <i className="ri-arrow-right-line text-white text-[12px]"></i>
          </div>
        </a>

        {/* Divider */}
        <div className="w-px h-6 bg-white/30 mx-2 flex-shrink-0"></div>

        {/* Phone */}
        <a
          href="tel:+13176482983"
          className="flex items-center gap-1.5 flex-shrink-0 cursor-pointer"
          aria-label="Call (317) 648-2983"
        >
          <i className="ri-phone-fill text-white text-[14px]"></i>
          <span className="text-white text-[11px] font-semibold whitespace-nowrap">Call Now</span>
        </a>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss quote bar"
          className="ml-2 w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity cursor-pointer bg-transparent border-none"
        >
          <i className="ri-close-line text-white text-[14px]"></i>
        </button>
      </div>
    </div>
  );
}
